import { EventAttributesActionLocation } from 'utils/analytics/amplitudeEventConstants';
import { RescheduleTo } from 'actions/RescheduleActions';
import { assertNever } from '@tock/shared/utils/typeUtils';
import { core } from '@tock/coretypes';
import HandSellType from 'constants/HandSellType';

export interface PurchaseOrderKeyValue {
  [attribute: string]: string;
}

// edit an existing reservation
export interface ReservationEdit {
  type: 'RESERVATION_EDIT';
  purchase: core.PastPurchase;
  ticketGroup: core.TicketGroup;
  reservationAttributes?: PurchaseOrderKeyValue;
}

// reschedule an existing reservation
export interface ReservationReschedule {
  type: 'RESERVATION_RESCHEDULE';
  purchase: core.PastPurchase;
  rescheduleTo: RescheduleTo;
  fromTicketGroup: core.TicketGroup;
  reservationAttributes?: PurchaseOrderKeyValue;
}

// start search flow from search
interface ReservationSearch {
  type: 'RESERVATION_SEARCH';
  reservationAttributes?: PurchaseOrderKeyValue;
  reservationType: HandSellType;
}

// start from a table
interface ReservationTableSearch {
  type: 'RESERVATION_SEARCH_TABLE';
  reservationAttributes?: PurchaseOrderKeyValue;
  reservationType: HandSellType;
}

// start from an unassigned/held/held for
interface ReservationDateTime {
  type: 'RESERVATION_DATE_TIME';
  subType?: ReservationSubtype;
  dateTime: string;
  reservationAttributes?: PurchaseOrderKeyValue;
  reservationType: HandSellType;
  ticketCount: number;
  ticketGroupId: number;
  ticketTypeId: number;
  patron?: core.Patron;
}

// start from a calendar square
export interface ReservationDateTimeTable {
  type: 'RESERVATION_DATE_TIME_TABLE';
  subType?: ReservationSubtype;
  dateTime: string;
  tableId: number[];
  ticketCount: number;
  ticketGroupId: number;
  ticketTypeId: number;
  reservationAttributes?: PurchaseOrderKeyValue;
  reservationType: HandSellType;
  patron?: core.Patron;
  seatingAreaId?: number | undefined;
}

interface ReservationSubtypeHeld {
  type: 'HELD';
  ticketSubsetId: number;
}

interface ReservationSubtypeHeldFor {
  type: 'HELD_FOR';
  ticketSubsetId: number;
  patron: core.Patron;
}

export type ReservationType =
  | ReservationSearch
  | ReservationDateTimeTable
  | ReservationDateTime
  | ReservationTableSearch
  | ReservationEdit
  | ReservationReschedule;

export type ReservationSubtype = ReservationSubtypeHeld | ReservationSubtypeHeldFor;

export interface ReservationOpenAction {
  type: 'RESERVATION_OPEN';
  payload: { reservationType: ReservationType; actionLocation?: EventAttributesActionLocation };
}

// This was introduced to help track down https://tock.atlassian.net/browse/TK-9759
export type ReservationCloseReason =
  | 'EXPLICIT_CLOSE' // User explicitly clicked the "X" in the corner
  | 'BOOK_NOW_SUCCESS' // "Pay Now" or "Pay Later"
  | 'BOOK_BY_EMAIL_SUCCESS'
  | 'BOOK_BY_SMS_SUCCESS'
  | 'BOOKING_SUCCESS_WITH_CONFIRMATION'
  // Unknown cases that we can fill in as they're more understood
  | 'UNKNOWN_C'
  | 'UNKNOWN_D'
  | 'UNKNOWN_E'
  | 'UNKNOWN_F';

export interface ReservationCloseAction {
  type: 'RESERVATION_CLOSE';
  payload: { reason: ReservationCloseReason };
}

export interface ReservationModifyOpenAction {
  type: 'RESERVATION_MODIFY_OPEN';
}

export interface ReservationModifyCloseAction {
  type: 'RESERVATION_MODIFY_CLOSE';
}

export interface ReservationMinimizeAction {
  type: 'RESERVATION_MINIMIZE';
}

export interface ReservationMaximizeAction {
  type: 'RESERVATION_MAXIMIZE';
}

export type ReservationActions =
  | ReservationOpenAction
  | ReservationCloseAction
  | ReservationModifyOpenAction
  | ReservationModifyCloseAction
  | ReservationMaximizeAction
  | ReservationMinimizeAction;

export function openReservation(
  reservationType: ReservationType,
  actionLocation?: EventAttributesActionLocation
): ReservationOpenAction {
  return {
    type: 'RESERVATION_OPEN',
    payload: { reservationType, actionLocation: actionLocation },
  };
}

export function closeReservation(reason: ReservationCloseReason): ReservationCloseAction {
  if (window.LOGGER) {
    switch (reason) {
      case 'EXPLICIT_CLOSE':
      case 'BOOK_NOW_SUCCESS':
      case 'BOOK_BY_EMAIL_SUCCESS':
      case 'BOOK_BY_SMS_SUCCESS':
      case 'BOOKING_SUCCESS_WITH_CONFIRMATION': {
        // Do nothing if we've hit a well-understood expected case
        break;
      }
      case 'UNKNOWN_C':
      case 'UNKNOWN_D':
      case 'UNKNOWN_E':
      case 'UNKNOWN_F': {
        // Trying to track down https://tock.atlassian.net/browse/TK-9759
        window.LOGGER?.warn(`Reservation modal closed for unknown reason: ${reason}`);
        break;
      }
      default:
        // When adding a new reason, force us to decide whether it's a known
        // or unknown case
        assertNever(reason);
    }
  }

  return {
    type: 'RESERVATION_CLOSE',
    payload: { reason },
  };
}

export function openModifyReservation(): ReservationModifyOpenAction {
  return {
    type: 'RESERVATION_MODIFY_OPEN',
  };
}

export function closeModifyReservation(): ReservationModifyCloseAction {
  return {
    type: 'RESERVATION_MODIFY_CLOSE',
  };
}

export function minimizeReservation(): ReservationMinimizeAction {
  return {
    type: 'RESERVATION_MINIMIZE',
  };
}

export function maximizeReservation(): ReservationMaximizeAction {
  return {
    type: 'RESERVATION_MAXIMIZE',
  };
}
